import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usersRpc, transformUser, transformUserDetail, type CancelSubscriptionResult } from '../api';

interface UseUsersOptions {
  query?: string;
  tier?: 'free' | 'power_rack' | 'trial';
  limit?: number;
  offset?: number;
}

export function useUsers(options: UseUsersOptions = {}) {
  const { query = '', tier, limit = 20, offset = 0 } = options;

  const queryResult = useQuery({
    queryKey: ['admin', 'users', query, tier, limit, offset],
    queryFn: async () => {
      const { data, error } = await usersRpc.searchUsers({
        p_query: query || undefined,
        p_tier: tier,
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return {
        users: data.users.map(transformUser),
        total: data.total,
      };
    },
    enabled: true,
  });

  return {
    users: queryResult.data?.users ?? [],
    total: queryResult.data?.total ?? 0,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}

export function useUserDetail(userId: string | undefined) {
  const queryResult = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID required');

      const { data, error } = await usersRpc.getUserDetail({
        p_user_id: userId,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return transformUserDetail(data);
    },
    enabled: !!userId,
  });

  return {
    user: queryResult.data,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
}

export function useUpdateUserTier() {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateTier = useCallback(
    async (userId: string, tier: 'free' | 'power_rack' | 'trial', trialDays?: number) => {
      setIsUpdating(true);
      try {
        const { error } = await usersRpc.updateUserTier({
          p_user_id: userId,
          p_tier: tier,
          p_trial_days: trialDays,
        });

        if (error) throw error;

        await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        return { success: true };
      } catch (err) {
        return { success: false, error: err as Error };
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient]
  );

  return { updateTier, isUpdating };
}

export function useExportUserData() {
  const [isExporting, setIsExporting] = useState(false);

  const exportData = useCallback(async (userId: string, includeAudit = false) => {
    setIsExporting(true);
    try {
      const { data, error } = await usersRpc.exportUserData({
        p_target_user_id: userId,
        p_include_audit: includeAudit,
      });

      if (error) throw error;

      // If we got a URL, open it
      if (data?.url) {
        window.open(data.url, '_blank');
      } else if (data?.export) {
        // Download JSON directly
        const blob = new Blob([JSON.stringify(data.export, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `user-export-${userId}-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err as Error };
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportData, isExporting };
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  const [isCancelling, setIsCancelling] = useState(false);
  const [result, setResult] = useState<CancelSubscriptionResult | null>(null);

  const cancelSubscription = useCallback(
    async (userId: string, cancelAtPeriodEnd = true) => {
      setIsCancelling(true);
      setResult(null);
      try {
        const { data, error } = await usersRpc.cancelSubscription({
          p_user_id: userId,
          p_cancel_at_period_end: cancelAtPeriodEnd,
        });

        if (error) throw error;
        if (!data) throw new Error('No data returned');

        setResult(data);

        // Only invalidate queries if subscription was actually changed
        if (data.status === 'success') {
          // Invalidate all relevant queries
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
            queryClient.invalidateQueries({ queryKey: ['admin', 'user'] }),
            queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] }),
          ]);
        }

        return { success: data.status === 'success', data };
      } catch (err) {
        const errorResult: CancelSubscriptionResult = {
          status: 'error',
          platform: null,
          message: err instanceof Error ? err.message : 'Unknown error occurred',
          effective_end: null,
        };
        setResult(errorResult);
        return { success: false, data: errorResult };
      } finally {
        setIsCancelling(false);
      }
    },
    [queryClient]
  );

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  return { cancelSubscription, isCancelling, result, clearResult };
}
